import classNames from 'classnames';
import { useCallback } from 'react';
import { Button, Heading, useIsMobile } from 'yoso';
import { breakpoints } from '~/components/Breakpoint/Breakpoint.hook';
import { BaseCard } from '~/components/Card';
import { useChallengeEvent } from '~/components/Challenge/Challenge.hooks';
import { useChallengeEventHistories } from '~/components/Challenge/Event/ChallengeEvent.hooks';
import { T } from '~/components/I18n';
import { Chevron } from '~/components/Icon/v2/Chevron';
import { useMember } from '~/components/Member/Member.hooks';
import { Link, LinkProps } from '~/components/Route/Link';
import { useCountdown } from '~/components/Util';
import styles from './ChallengeEventCard.module.scss';
import motionCoinsMobile from './motionCoinsMO.animated.png';
import motionCoinsPc from './motionCoinsPC.animated.png';

export type ChallengeEventCardProps = {
  className?: string;
  onClick?: () => void;
};

export const ChallengeEventCard = ({ className, onClick }: ChallengeEventCardProps) => {
  const isMobile = useIsMobile(breakpoints, breakpoints.medium);

  const {
    data: { isLoggedIn },
  } = useMember();
  const { data, isFetched } = useChallengeEventHistories({
    enabled: isLoggedIn,
    useErrorBoundary: false,
  });
  const pages = data?.pages || [];
  const isPageEmpty = pages[0]?.histories.length === 0;
  const {
    data: { endAt, week },
  } = useChallengeEvent();
  const { days, hours, minutes, seconds } = useCountdown(endAt);

  const onButtonClick = useCallback((e) => {
    e.stopPropagation();
  }, []);

  const ref = useCallback(
    (element: HTMLImageElement) => {
      if (element) element.src = isMobile ? motionCoinsMobile : motionCoinsPc;
    },
    [isMobile]
  );

  return (
    <BaseCard size="large" className={classNames(styles.root, className)} onClick={onClick}>
      <img ref={ref} className={styles.image} alt="kaia" />
      <Heading level={4} className={styles.title}>
        <small>
          <T name="citizen_challenge_section4_subtitle" />
        </small>
        <T name="citizen_challenge_section4_title" />
      </Heading>
      {isFetched && !isPageEmpty && (
        <Link className={styles.link} to="/challenge" onClick={onButtonClick}>
          <T name="citizen_home_challenge_history_btn" />
          <Chevron className={styles.icon} />
        </Link>
      )}
      <p className={styles.countdown}>
        <T name="citizen_home_challenge_round" options={[week]} />{' '}
        <T
          name="citizen_home_challenge_countdown"
          options={[
            `${days.toString().padStart(2, '0')}:${hours.toString().padStart(2, '0')}:${minutes
              .toString()
              .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`,
          ]}
        />
      </p>
      <div className={styles.actions}>
        <Button<LinkProps>
          as={Link}
          to="/challenge/leaderboard"
          onClick={onButtonClick}
          className={styles.rewardButton}
          data-event="click"
          data-event-target="challenge_banner_beforejoin">
          <T name="citizen_challenge_section4_label" />
        </Button>
        {isFetched && !isPageEmpty && (
          <Button<LinkProps>
            as={Link}
            variant="outlined"
            className={styles.eventButton}
            onClick={onButtonClick}
            to="/challenge">
            <T name="citizen_home_challenge_history_btn" />
          </Button>
        )}
      </div>
    </BaseCard>
  );
};
